import "./CreateForm.css";

import { Button, Card, Divider, Input, Space, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";

import { type FieldType, type FormBuilderProps } from "../../../_lib/_store";
import { FieldEditor } from "./CreateForm.FieldEditor";
import { FieldTypeSelector } from "./CreateForm.FieldTypeSelector";
import { FormPreview } from "./CreateForm.FormPreview";

const { Text, Title } = Typography;

const DEFAULT_SECTIONS = [
  "Personal Information",
  "Contact Details",
  "Education",
  "Work Experience",
];

export const FormBuilder = ({ value, onChange }: FormBuilderProps) => {
  const [fields, setFields] = useState<FieldType[]>(() => {
    if (value && value.fields && Array.isArray(value.fields)) {
      return value.fields;
    }
    return [];
  });

  // Extract unique sections from existing fields and combine with defaults
  const [sections, setSections] = useState<string[]>(() => {
    const existingSections =
      value?.fields && Array.isArray(value.fields)
        ? Array.from(
            new Set(
              value.fields.flatMap((field: FieldType) => field.sections || []),
            ),
          )
        : [];
    return Array.from(new Set([...DEFAULT_SECTIONS, ...existingSections]));
  });
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [googleDocUrl, setGoogleDocUrl] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (value && value.fields && Array.isArray(value.fields)) {
      setFields(value.fields);

      // Extract and update sections from the new value
      const existingSections = Array.from(
        new Set(
          value.fields.flatMap((field: FieldType) => field.sections || []),
        ),
      );
      const updatedSections = Array.from(
        new Set([...DEFAULT_SECTIONS, ...existingSections]),
      );
      setSections(updatedSections);
    }
  }, [value]);
  const updateFormData = useCallback(
    (newFields: FieldType[]) => {
      setFields(newFields);
      onChange?.({ fields: newFields });
    },
    [onChange],
  );

  const addField = (type: string) => {
    if (
      [
        "text",
        "email",
        "number",
        "textarea",
        "select",
        "radio",
        "checkbox",
        "date",
        "file",
      ].includes(type)
    ) {
      const newField: FieldType = {
        id: `field_${Date.now()}`,
        type: type as FieldType["type"],
        label: `New ${type} field`,
        placeholder: type === "file" ? "" : `Enter ${type}...`,
        required: false,
        sections: [],
        options:
          type === "select" || type === "radio" || type === "checkbox"
            ? ["Option 1", "Option 2"]
            : undefined,
      };
      updateFormData([...fields, newField]);
    }
  };

  const moveFieldUp = (index: number) => {
    if (index > 0) {
      const newFields = [...fields];
      [newFields[index - 1], newFields[index]] = [
        newFields[index],
        newFields[index - 1],
      ];
      updateFormData(newFields);
    }
  };

  const moveFieldDown = (index: number) => {
    if (index < fields.length - 1) {
      const newFields = [...fields];
      [newFields[index], newFields[index + 1]] = [
        newFields[index + 1],
        newFields[index],
      ];
      updateFormData(newFields);
    }
  };

  const processDocument = async () => {
    if (!googleDocUrl.trim()) return;

    setProcessing(true);
    setProcessingMessage("Processing document...");

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const generatedFields: FieldType[] = [
        {
          id: `field_${Date.now()}_1`,
          type: "text",
          label: "Full Name",
          placeholder: "Enter your full name",
          required: true,
          sections: ["Personal Information"],
        },
        {
          id: `field_${Date.now()}_2`,
          type: "email",
          label: "Email Address",
          placeholder: "Enter your email",
          required: true,
          sections: ["Contact Details"],
        },
      ];

      setFields([...fields, ...generatedFields]);
      setHasUnsavedChanges(true);
      setProcessingMessage("Success! Generated fields from document.");
    } catch (error) {
      setProcessingMessage("Error processing document. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const saveChanges = () => {
    onChange?.({ fields });
    setHasUnsavedChanges(false);
    setProcessingMessage("Changes applied successfully!");
  };
  return (
    <div>
      <Card className="form-builder-controls" size="small">
        <Space className="form-builder-controls-space">
          <FieldTypeSelector onAddField={addField} />
          <Button
            type={showDocumentUpload ? "default" : "primary"}
            onClick={() => setShowDocumentUpload(!showDocumentUpload)}
          >
            Generate from Document
          </Button>
        </Space>

        {showDocumentUpload && (
          <Card className="document-upload-card">
            <Text strong className="document-upload-title">
              Generate Fields from Document
            </Text>
            <Text type="secondary" className="document-upload-description">
              Provide a Google Docs URL to automatically generate form fields
              using AI.
            </Text>
            <div className="document-url-container">
              <Text type="secondary" className="document-url-label">
                Enter a Google Docs URL (e.g.,
                https://docs.google.com/document/d/...)
              </Text>
              <Input
                type="url"
                value={googleDocUrl}
                onChange={(e) => setGoogleDocUrl(e.target.value)}
                placeholder="https://docs.google.com/document/d/..."
                size="large"
              />
            </div>

            <div className="document-actions">
              <Button
                type="primary"
                onClick={processDocument}
                disabled={processing || !googleDocUrl.trim()}
                loading={processing}
              >
                {processing ? "Processing..." : "Generate Fields"}
              </Button>

              {processingMessage && (
                <Text
                  className={`document-message ${
                    processingMessage.includes("Success")
                      ? "document-message--success"
                      : processingMessage.includes("Processing")
                        ? "document-message--processing"
                        : "document-message--error"
                  }`}
                >
                  {processingMessage}
                </Text>
              )}
            </div>

            {hasUnsavedChanges && (
              <div className="unsaved-changes">
                <div className="unsaved-changes-content">
                  <Text className="unsaved-changes-text">
                    ⚠️ You have unsaved changes from document generation
                  </Text>
                  <Button type="primary" size="small" onClick={saveChanges}>
                    Apply Changes
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </Card>
      <Card className="fields-editor">
        {fields.length === 0 ? (
          <Text type="secondary">
            No fields added yet. Click the buttons above to add form fields.
          </Text>
        ) : (
          fields.map((field, index) => (
            <FieldEditor
              key={field.id}
              field={field}
              index={index}
              onUpdate={(key, value) =>
                updateFormData(
                  fields.map((f, i) =>
                    i === index ? { ...f, [key]: value } : f,
                  ),
                )
              }
              onRemove={() =>
                updateFormData(
                  fields.filter((_: FieldType, i: number) => i !== index),
                )
              }
              onMoveUp={index > 0 ? () => moveFieldUp(index) : undefined}
              onMoveDown={
                index < fields.length - 1
                  ? () => moveFieldDown(index)
                  : undefined
              }
              onAddOption={() =>
                updateFormData(
                  fields.map((f, i) =>
                    i === index
                      ? { ...f, options: [...(f.options || []), "New Option"] }
                      : f,
                  ),
                )
              }
              onUpdateOption={(optionIndex, newValue) =>
                updateFormData(
                  fields.map((f, i) =>
                    i === index && f.options
                      ? {
                          ...f,
                          options: f.options.map((opt, j) =>
                            j === optionIndex ? newValue : opt,
                          ),
                        }
                      : f,
                  ),
                )
              }
              onRemoveOption={(optionIndex) =>
                updateFormData(
                  fields.map((f, i) =>
                    i === index && f.options
                      ? {
                          ...f,
                          options: f.options.filter(
                            (_: string, j: number) => j !== optionIndex,
                          ),
                        }
                      : f,
                  ),
                )
              }
              isSelected={false}
              sections={sections}
              onUpdateSections={(i, newSections) =>
                updateFormData(
                  fields.map((f, idx) =>
                    idx === i ? { ...f, sections: newSections } : f,
                  ),
                )
              }
              onSectionsListUpdate={(newSection) =>
                setSections((prev) =>
                  Array.from(new Set([...prev, newSection])),
                )
              }
            />
          ))
        )}
      </Card>
      <Card className="form-preview">
        <Title level={3}>Form Preview</Title>
        {fields.length === 0 ? (
          <div style={{ textAlign: "center", color: "#999", padding: "40px" }}>
            No form fields yet. Add fields above to see the preview.
          </div>
        ) : (
          <div>
            {sections.map((section: string) => {
              const sectionFields = fields.filter((f: FieldType) =>
                f.sections.includes(section),
              );
              if (sectionFields.length === 0) return null;

              return (
                <div key={section} style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    {section}
                  </Text>
                  <Divider />
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {sectionFields.map((field) => (
                      <FormPreview
                        key={field.id}
                        field={{
                          ...field,
                          options: field.options ?? undefined,
                        }}
                      />
                    ))}
                  </Space>
                </div>
              );
            })}
            {(() => {
              const fieldsWithoutSections = fields.filter(
                (f: FieldType) => f.sections.length === 0,
              );
              if (fieldsWithoutSections.length === 0) return null;

              return (
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Other Fields
                  </Text>
                  <Divider />
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {fieldsWithoutSections.map((field) => (
                      <FormPreview
                        key={field.id}
                        field={{
                          ...field,
                          options: field.options ?? undefined,
                        }}
                      />
                    ))}
                  </Space>
                </div>
              );
            })()}
          </div>
        )}
      </Card>
    </div>
  );
};
